import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import { GO_LIVE_ERRORS } from '@/modules/instant-sessions/utils/liveText'
import { isPhoneBrowser } from '@/modules/instant-sessions/utils/isPhoneBrowser'
import { router } from '@inertiajs/vue3'
import type {
  InstantChannelMessage,
  InstantPerson,
  InstantRequestPayload,
  InstantSessionsShared,
} from '@/modules/instant-sessions/types'
import {
  askNotificationPermission,
  showBrowserNotification,
  startRing,
  startTitleFlash,
  stopRing,
  stopTitleFlash,
  unlockAudio,
} from '@/modules/instant-sessions/utils/alerts'

// One place for the instant-session state that the nav toggle, the "Live now"
// row, the mentor's request card and the member's waiting card all read.
export const useInstantSessionsStore = defineStore('instantSessions', () => {
  const enabled = ref(false)
  const canGoLive = ref(false)
  const canRequest = ref(false)
  const liveUntil = ref<string | null>(null)
  // In an instant call now: the nav pill says "In a call".
  const inCall = ref(false)
  const incoming = ref<InstantRequestPayload | null>(null)
  const outgoing = ref<InstantRequestPayload | null>(null)
  // The member's last request that nobody answered, kept so the line is not lost.
  const unanswered = ref<{ mentor: InstantPerson; message: string } | null>(
    null,
  )
  const liveOffReason = ref<string | null>(null)
  const isSaving = ref(false)
  // The "Live now" row refreshes when this moves.
  const rowVersion = ref(0)

  const isLive = computed(
    () => !!liveUntil.value && new Date(liveUntil.value) > new Date(),
  )

  const hydrate = (shared: InstantSessionsShared | null | undefined) => {
    enabled.value = !!shared
    if (!shared) return
    canGoLive.value = shared.canGoLive
    canRequest.value = shared.canRequest
    liveUntil.value = shared.liveUntil
    inCall.value = !!shared.inCall
    if (shared.incoming && !incoming.value) receiveIncoming(shared.incoming)
    if (!shared.incoming && incoming.value) closeIncoming()
    outgoing.value = shared.outgoing
  }

  const goLiveConfirmationOpen = ref(false)
  const goLiveError = ref<string | null>(null)

  const requestGoLive = () => {
    if (
      !enabled.value ||
      !canGoLive.value ||
      inCall.value ||
      isLive.value ||
      isSaving.value ||
      isPhoneBrowser()
    )
      return
    goLiveError.value = null
    goLiveConfirmationOpen.value = true
  }
  const cancelGoLive = () => {
    goLiveConfirmationOpen.value = false
    goLiveError.value = null
  }
  const confirmGoLive = async () => {
    if (!goLiveConfirmationOpen.value || isSaving.value) return
    if (
      !enabled.value ||
      !canGoLive.value ||
      inCall.value ||
      isPhoneBrowser()
    ) {
      goLiveError.value =
        'You cannot go live here right now. Open Live now on a computer and try again.'
      return
    }
    if (isLive.value) {
      cancelGoLive()
      return
    }
    goLiveError.value = null
    const result = await setLive(true)
    if (result.ok) cancelGoLive()
    else
      goLiveError.value =
        GO_LIVE_ERRORS[result.error ?? ''] ??
        'Could not go live. Please try again.'
  }

  const setLive = async (live: boolean) => {
    if (isSaving.value) return { ok: false as const, error: null }
    if (live) unlockAudio()
    isSaving.value = true
    try {
      const { data } = await axios.patch('/instant/presence', { live })
      liveUntil.value = data.liveUntil
      liveOffReason.value = null
      if (live) askNotificationPermission()
      return { ok: true as const }
    } catch (error) {
      const code = axios.isAxiosError(error)
        ? error.response?.data?.error
        : null
      return { ok: false as const, error: code as string | null }
    } finally {
      isSaving.value = false
    }
  }

  const receiveIncoming = (request: InstantRequestPayload) => {
    incoming.value = request
    startRing(request.expiresAt)
    startTitleFlash(() => {
      const seconds = Math.max(
        0,
        Math.round((new Date(request.expiresAt).getTime() - Date.now()) / 1000),
      )
      return `Talk request · ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    })
    showBrowserNotification(
      `${request.mentee.firstName} wants to talk now`,
      request.message,
    )
  }

  const closeIncoming = () => {
    stopRing()
    incoming.value = null
    answerError.value = null
    stopTitleFlash()
  }

  const sendRequest = async (mentorId: number, message: string) => {
    unanswered.value = null
    const { data } = await axios.post('/instant/requests', {
      mentor_id: mentorId,
      message,
    })
    outgoing.value = data.request
    return data.request as InstantRequestPayload
  }

  const cancelOutgoing = async () => {
    if (!outgoing.value) return
    const id = outgoing.value.id
    outgoing.value = null
    rowVersion.value += 1
    try {
      await axios.patch(`/instant/requests/${id}/cancel`)
    } catch {
      // The mentor answered or the request timed out first. The channel says which.
    }
  }

  const isAnswering = ref(false)
  const answerError = ref<string | null>(null)

  // The card stays open until the server answers, so a failed join is never
  // silent. A request that ended first (timeout, cancel, 404) just closes.
  const answer = async (action: 'join' | 'not-now') => {
    if (!incoming.value || isAnswering.value) return
    const id = incoming.value.id
    isAnswering.value = true
    answerError.value = null
    try {
      const { data } = await axios.patch(`/instant/requests/${id}/${action}`)
      closeIncoming()
      if (action === 'join' && data.callUrl) router.visit(data.callUrl)
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : null
      if (status === 404 || status === 410) {
        closeIncoming()
      } else {
        answerError.value = 'Could not join the call. Try again.'
      }
    } finally {
      isAnswering.value = false
    }
  }

  // The session page of one call, for the mentee or for the mentor.
  const SESSION_PAGE = /^\/(?:mentor\/)?sessions\/(\d+)\/?$/

  // The call is over on the server. A person who left the room early is on
  // the session page, which still says the call is running and shows no
  // review form. Read the page again, once. The message can arrive while the
  // browser still goes from the room to the session page, so look twice.
  const refreshSessionPage = (courseId: number) => {
    let done = false
    const reloadIfThere = () => {
      if (done) return
      const match = window.location.pathname.match(SESSION_PAGE)
      if (!match || Number(match[1]) !== courseId) return
      done = true
      router.reload()
    }
    reloadIfThere()
    window.setTimeout(reloadIfThere, 2500)
  }

  const handleMessage = (message: InstantChannelMessage) => {
    switch (message.type) {
      case 'live':
        liveUntil.value = message.live ? (message.liveUntil ?? null) : null
        liveOffReason.value = message.live ? null : (message.reason ?? null)
        break
      case 'incoming':
        receiveIncoming(message.request)
        break
      case 'closed':
        if (incoming.value?.id === message.requestId) closeIncoming()
        if (outgoing.value?.id === message.requestId) outgoing.value = null
        break
      case 'unanswered':
        if (outgoing.value?.id === message.requestId) {
          unanswered.value = {
            mentor: message.mentor,
            message: outgoing.value.message,
          }
          outgoing.value = null
          rowVersion.value += 1
        }
        break
      case 'joined':
        inCall.value = true
        if (outgoing.value?.id === message.requestId) {
          outgoing.value = null
          router.visit(message.callUrl)
        }
        if (incoming.value?.id === message.requestId) closeIncoming()
        break
      case 'call_finished':
        inCall.value = false
        refreshSessionPage(message.call.courseId)
        break
    }
  }

  return {
    enabled,
    canGoLive,
    canRequest,
    liveUntil,
    inCall,
    incoming,
    outgoing,
    unanswered,
    liveOffReason,
    isSaving,
    rowVersion,
    isLive,
    hydrate,
    setLive,
    requestGoLive,
    confirmGoLive,
    cancelGoLive,
    goLiveConfirmationOpen,
    goLiveError,
    sendRequest,
    cancelOutgoing,
    answer,
    isAnswering,
    answerError,
    handleMessage,
  }
})
